import React, {useState, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    TextField,
    Button,
    Box,
    Alert,
    Grid,
    Divider,
    CircularProgress
} from '@mui/material';
import {useCart} from '../context/CartContext';
import {checkout} from '../services/api';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

function Checkout() {
    const navigate = useNavigate();
    const {cartItems, clearCart} = useCart();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [shippingData, setShippingData] = useState({
        streetNumber: '',
        streetName: '',
        city: '',
        state: '',
        postalCode: '',
        additionalInfo: ''
    });

    useEffect(() => {
        if (!cartItems || cartItems.length === 0) {
            navigate('/cart');
        }
    }, [cartItems, navigate]);

    const handleChange = (e) => {
        setShippingData({
            ...shippingData,
            [e.target.name]: e.target.value
        });
        if (error) setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        if (!shippingData.streetNumber || !shippingData.streetName ||
            !shippingData.city || !shippingData.state) {
            setError('Please fill in all required address fields');
            setLoading(false);
            return;
        }

        try {
            const fullAddress = `${shippingData.streetNumber} ${shippingData.streetName}, ${shippingData.city}, ${shippingData.state}${shippingData.postalCode ? ', ' + shippingData.postalCode : ''}${shippingData.additionalInfo ? '. ' + shippingData.additionalInfo : ''}`;

            const orderData = {
                shipping_address: fullAddress
            };

            console.log('Submitting order:', orderData);
            const response = await checkout(orderData);

            if (response.data?.payment_link) {
                console.log('Redirecting to:', response.data.payment_link);
                window.location.href = response.data.payment_link;
            } else {
                console.error('No payment link in response:', response);
                setError('Payment initialization failed. Please try again.');
            }
        } catch (err) {
            console.error('Checkout error:', err);
            console.error('Error response:', err.response);
            setError(err.response?.data?.message || err.response?.data?.error || 'Failed to process checkout');
        } finally {
            setLoading(false);
        }
    };


    const cartTotal = cartItems?.reduce((total, item) => {
        return total + ((item.product?.price || 0) * item.quantity);
    }, 0) || 0;

    if (cartItems === null || cartItems === undefined) {
        return (
            <Container maxWidth="md" sx={{mt: 4, display: 'flex', justifyContent: 'center'}}>
                <CircularProgress/>
            </Container>
        );
    }

    const deliveryFee = 7000;
    const totalWithDelivery = cartTotal + deliveryFee;

    return (
        <Container maxWidth="md" sx={{mt: 4, mb: 4}}>
            <Paper elevation={3} sx={{p: 4}}>
                <Typography variant="h4" gutterBottom fontWeight="bold">
                    Checkout
                </Typography>

                {error && (
                    <Alert severity="error" onClose={() => setError('')} sx={{mb: 3}}>
                        {error}
                    </Alert>
                )}

                <Alert
                    severity="info"
                    icon={<LocalShippingIcon/>}
                    sx={{mb: 3, fontWeight: 'bold'}}
                >
                    You will be charged ₦7,000 delivery fee per order
                </Alert>

                <Box sx={{mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1}}>
                    <Typography variant="h6" gutterBottom>
                        Order Summary
                    </Typography>
                    <Box sx={{display: 'flex', justifyContent: 'space-between', mb: 1}}>
                        <Typography>Subtotal ({cartItems.length} items):</Typography>
                        <Typography fontWeight="bold">₦{cartTotal.toLocaleString()}</Typography>
                    </Box>
                    <Box sx={{display: 'flex', justifyContent: 'space-between', mb: 1}}>
                        <Typography>Delivery Fee:</Typography>
                        <Typography fontWeight="bold">₦{deliveryFee.toLocaleString()}</Typography>
                    </Box>
                    <Divider sx={{my: 1}}/>
                    <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                        <Typography variant="h6">Total:</Typography>
                        <Typography variant="h6" color="primary" fontWeight="bold">
                            ₦{totalWithDelivery.toLocaleString()}
                        </Typography>
                    </Box>
                </Box>

                <Box component="form" onSubmit={handleSubmit} noValidate>
                    <Typography variant="h6" gutterBottom sx={{mt: 3}}>
                        Shipping Address
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                required
                                fullWidth
                                label="Street Number"
                                name="streetNumber"
                                placeholder="e.g., 123"
                                value={shippingData.streetNumber}
                                onChange={handleChange}
                                error={!!error && !shippingData.streetNumber}
                            />
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            <TextField
                                required
                                fullWidth
                                label="Street Name"
                                name="streetName"
                                placeholder="e.g., Main Street"
                                value={shippingData.streetName}
                                onChange={handleChange}
                                error={!!error && !shippingData.streetName}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required
                                fullWidth
                                label="City"
                                name="city"
                                placeholder="e.g., Lagos"
                                value={shippingData.city}
                                onChange={handleChange}
                                error={!!error && !shippingData.city}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required
                                fullWidth
                                label="State"
                                name="state"
                                placeholder="e.g., Lagos State"
                                value={shippingData.state}
                                onChange={handleChange}
                                error={!!error && !shippingData.state}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Postal Code (Optional)"
                                name="postalCode"
                                placeholder="e.g., 100001"
                                value={shippingData.postalCode}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Additional Information (Optional)"
                                name="additionalInfo"
                                placeholder="Landmark, building description, etc."
                                multiline
                                rows={2}
                                value={shippingData.additionalInfo}
                                onChange={handleChange}
                            />
                        </Grid>
                    </Grid>

                    <Box sx={{mt: 4, display: 'flex', gap: 2}}>
                        <Button
                            variant="outlined"
                            onClick={() => navigate('/cart')}
                            disabled={loading}
                            sx={{flex: 1}}
                        >
                            Back to Cart
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={loading}
                            sx={{
                                flex: 2,
                                py: 1.5,
                                fontWeight: 'bold'
                            }}
                        >
                            {loading ? 'Processing...' : `Proceed to Payment (₦${totalWithDelivery.toLocaleString()})`}
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </Container>
    );
}

export default Checkout;
